import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraOverlayDialog, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { useOpportunityRfpExpected, useSetOpportunityRfpExpected } from './opportunityHooks';
export const RFP_EXPECTED_BASES = [
    {
        value: 'customer_said',
        label: 'Customer said'
    },
    {
        value: 'agency_forecast',
        label: 'Agency forecast'
    },
    {
        value: 'notice',
        label: 'Notice'
    },
    {
        value: 'our_estimate',
        label: 'Our estimate'
    }
];
const DEFAULT_BASIS = 'our_estimate';
function isBasis(value) {
    return RFP_EXPECTED_BASES.some((b)=>b.value === value);
}
/**
 * GRW-06: set or clear when the RFP is expected on the pursuit's opportunity.
 * Opened from the facts rail "RFP expected" pencil (editors with a linked
 * opportunity). Saves with the version it read; a concurrent change comes
 * back as a conflict, the stored value is reloaded, and nothing is lost.
 */ export function RfpExpectedDialog({ opportunityId, open, onOpenChange }) {
    const stored = useOpportunityRfpExpected(opportunityId, open);
    const save = useSetOpportunityRfpExpected();
    const [date, setDate] = useState('');
    const [basis, setBasis] = useState(DEFAULT_BASIS);
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const ids = {
        date: useId(),
        basis: useId(),
        note: useId()
    };
    const value = stored.data;
    useEffect(()=>{
        if (!open) return;
        setDate(value?.rfpExpectedOn ?? '');
        setBasis(value?.basis && isBasis(value.basis) ? value.basis : DEFAULT_BASIS);
        setNote(value?.note ?? '');
    }, [
        open,
        value
    ]);
    useEffect(()=>{
        if (open) setError('');
    }, [
        open
    ]);
    const submit = ()=>{
        setError('');
        save.mutate({
            opportunityId,
            rfpExpectedOn: date,
            basis: date ? basis : '',
            note: date ? note.trim() : '',
            expectedVersion: value?.version ?? 0
        }, {
            onSuccess: ()=>{
                toast.success(date ? 'RFP expected date saved' : 'RFP expected date cleared');
                onOpenChange(false);
            },
            onError: (err)=>{
                if (ConnectError.from(err).code === Code.Aborted) {
                    setError('Someone changed this date while you were editing. Their value is shown now.');
                    return;
                }
                setError(err instanceof Error ? err.message : 'The date could not be saved.');
            }
        });
    };
    const loading = stored.isLoading;
    const unreadable = stored.isError;
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        description: "When the customer is expected to release the RFP. Shown on the pursuit and the Growth Home.",
        footerProps: {
            cancelLabel: 'Cancel',
            onCancel: ()=>onOpenChange(false),
            confirmLabel: date ? 'Save' : 'Clear date',
            onConfirm: submit,
            confirmDisabled: loading || unreadable || !date && !value?.rfpExpectedOn,
            loading: save.isPending,
            loadingLabel: 'Saving…',
            confirmProps: {
                'data-testid': Testids.rfpExpected.submit
            }
        },
        onOpenChange: onOpenChange,
        open: open,
        size: "sm",
        testId: Testids.rfpExpected.dialog,
        title: "RFP expected",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                loading && /*#__PURE__*/ _jsx(SpecraText, {
                    size: "caption",
                    tone: "muted",
                    children: "Loading the current date…"
                }),
                unreadable && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    variant: "framed",
                    children: "The current date could not be loaded. Close this and try again."
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    hint: "Leave empty to clear it.",
                    htmlFor: ids.date,
                    label: "Date",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        "data-testid": Testids.rfpExpected.dateInput,
                        disabled: loading || unreadable,
                        id: ids.date,
                        onChange: (e)=>setDate(e.target.value),
                        type: "date",
                        value: date
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    htmlFor: ids.basis,
                    label: "Based on",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        "data-testid": Testids.rfpExpected.basisSelect,
                        disabled: loading || unreadable || !date,
                        id: ids.basis,
                        onChange: (e)=>setBasis(e.target.value),
                        value: basis,
                        children: RFP_EXPECTED_BASES.map((b)=>/*#__PURE__*/ _jsx("option", {
                                value: b.value,
                                children: b.label
                            }, b.value))
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    htmlFor: ids.note,
                    label: "Note",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        "data-testid": Testids.rfpExpected.noteInput,
                        disabled: loading || unreadable || !date,
                        id: ids.note,
                        onChange: (e)=>setNote(e.target.value),
                        rows: 3,
                        value: note
                    })
                }),
                error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    variant: "framed",
                    children: error
                })
            ]
        })
    });
}
